// The Fanbase Fix - shared types (Stage 1, deterministic only)

export const QUESTIONNAIRE_VERSION = 'fbf-q-2026-09-21';
export const RULES_VERSION = 'fbf-rules-2026-09-21';

export type Category =
  | 'AUDIENCE'
  | 'DISCOVERY'
  | 'CONTENT'
  | 'NEXT_STEP'
  | 'RETURN'
  | 'TESTING'
  | 'BASELINE';

export type GoalId =
  | 'FIRST_LISTENERS'
  | 'MORE_DISCOVERY'
  | 'NEXT_RELEASE'
  | 'MORE_RETURN'
  | 'LOCAL_SHOW';

export type ReadinessId =
  | 'RELEASED'
  | 'UNRELEASED_SHAREABLE'
  | 'CHOOSE_SONG'
  | 'NO_SHAREABLE';

export type AudienceId =
  | 'SPECIFIC_REASON'
  | 'REFERENCE_NAMES'
  | 'TOPIC_FIRST'
  | 'EVERYONE'
  | 'UNSURE';

export type DiscoveryId =
  | 'OWN_CONTENT'
  | 'LIVE'
  | 'COLLAB'
  | 'CURATION'
  | 'ADS'
  | 'EXISTING_CIRCLE'
  | 'SCATTERED'
  | 'NOT_STARTED'
  | 'UNKNOWN';

export type IntroductionId =
  | 'ANNOUNCEMENTS'
  | 'SONG_LED'
  | 'SONG_CONTEXT'
  | 'OTHER_CONTENT'
  | 'FULL_MUSIC'
  | 'VERY_LITTLE'
  | 'NOT_ONLINE';

export type ResponseId =
  | 'LIGHT_ATTENTION'
  | 'SONG_ENQUIRIES'
  | 'DELIBERATE_INTEREST'
  | 'RETURNING_INTEREST'
  | 'NO_OBSERVED_RESPONSE'
  | 'NO_RECENT_ATTEMPT'
  | 'UNKNOWN';

export type NextStepId =
  | 'CLEAR_DESTINATION'
  | 'GENERAL_PROFILE'
  | 'MANY_ACTIONS'
  | 'NO_ROUTE'
  | 'UNRELEASED_UNCLEAR'
  | 'UNKNOWN';

export type ReturnId =
  | 'RELATED_FOLLOW_UP'
  | 'SAME_REQUEST'
  | 'MOVE_ON'
  | 'NO_FOLLOW_UP'
  | 'NOT_YET';

export type LearningId =
  | 'COMPARE'
  | 'REPEAT_FEELING'
  | 'CHANGE_EVERYTHING'
  | 'VIEWS_ONLY'
  | 'RANDOM'
  | 'TOO_EARLY';

export type CapacityId = 'TINY' | 'SMALL' | 'MEDIUM' | 'LARGE';

/**
 * Every question accepts more than one answer, so every field is a list.
 * Contradictory options clear their siblings in the UI (see questions.ts).
 */
export interface Answers {
  goal: GoalId[];
  readiness: ReadinessId[];
  songTitle: string;
  audience: AudienceId[];
  discovery: DiscoveryId[];
  introduction: IntroductionId[];
  response: ResponseId[];
  nextStep: NextStepId[];
  return: ReturnId[];
  learning: LearningId[];
  capacity: CapacityId[];
  /** Targeted personalisation, keyed by field id. */
  personalisation: Record<string, string>;
}

export const emptyAnswers = (): Answers => ({
  goal: [],
  readiness: [],
  songTitle: '',
  audience: [],
  discovery: [],
  introduction: [],
  response: [],
  nextStep: [],
  return: [],
  learning: [],
  capacity: [],
  personalisation: {},
});

/**
 * Single-value view of the answers, used for writing the result copy.
 * The selector still reads every answer; this only decides which one the
 * prepared material is written around.
 */
export interface ResolvedAnswers {
  goal: GoalId | null;
  readiness: ReadinessId | null;
  songTitle: string;
  audience: AudienceId | null;
  discovery: DiscoveryId | null;
  introduction: IntroductionId[];
  response: ResponseId[];
  nextStep: NextStepId | null;
  return: ReturnId | null;
  learning: LearningId | null;
  capacity: CapacityId | null;
  personalisation: Record<string, string>;
}

/** Most limiting answer first. The plan is written around the hardest case. */
const DOMINANCE = {
  goal: ['FIRST_LISTENERS', 'MORE_DISCOVERY', 'NEXT_RELEASE', 'MORE_RETURN', 'LOCAL_SHOW'],
  audience: ['EVERYONE', 'UNSURE', 'REFERENCE_NAMES', 'TOPIC_FIRST', 'SPECIFIC_REASON'],
  discovery: ['NOT_STARTED', 'EXISTING_CIRCLE', 'UNKNOWN', 'SCATTERED', 'ADS', 'CURATION', 'COLLAB', 'LIVE', 'OWN_CONTENT'],
  nextStep: ['UNRELEASED_UNCLEAR', 'NO_ROUTE', 'UNKNOWN', 'MANY_ACTIONS', 'GENERAL_PROFILE', 'CLEAR_DESTINATION'],
  return: ['NOT_YET', 'NO_FOLLOW_UP', 'MOVE_ON', 'SAME_REQUEST', 'RELATED_FOLLOW_UP'],
  learning: ['TOO_EARLY', 'RANDOM', 'CHANGE_EVERYTHING', 'VIEWS_ONLY', 'REPEAT_FEELING', 'COMPARE'],
} as const;

/** Where the music is: work from the most advanced thing they have. */
export const READINESS_ORDER: ReadinessId[] = ['RELEASED', 'UNRELEASED_SHAREABLE', 'CHOOSE_SONG', 'NO_SHAREABLE'];
/** Time per week: work from the smallest amount they picked. */
export const CAPACITY_ORDER: CapacityId[] = ['TINY', 'SMALL', 'MEDIUM', 'LARGE'];

const pick = <T extends string>(list: T[], order: readonly string[]): T | null => {
  if (!list.length) return null;
  const sorted = [...list].sort((a, b) => order.indexOf(a) - order.indexOf(b));
  return sorted[0];
};

export const resolveAnswers = (a: Answers): ResolvedAnswers => ({
  goal: pick(a.goal, DOMINANCE.goal),
  readiness: pick(a.readiness, READINESS_ORDER),
  songTitle: a.songTitle,
  audience: pick(a.audience, DOMINANCE.audience),
  discovery: pick(a.discovery, DOMINANCE.discovery),
  introduction: a.introduction,
  response: a.response,
  nextStep: pick(a.nextStep, DOMINANCE.nextStep),
  return: pick(a.return, DOMINANCE.return),
  learning: pick(a.learning, DOMINANCE.learning),
  capacity: pick(a.capacity, CAPACITY_ORDER),
  personalisation: a.personalisation,
});

export type PlaybookId =
  | 'A01' | 'A02' | 'A03'
  | 'D01' | 'D02' | 'D03'
  | 'C01' | 'C02' | 'C03'
  | 'N01' | 'N02' | 'N03'
  | 'R01' | 'R02' | 'R03'
  | 'T01' | 'T02' | 'T03'
  | 'B01' | 'B02' | 'NO_CLEAR_GAP';

export type BasisType =
  | 'SPECIFIC_REPORTED_EXAMPLE'
  | 'ANSWER_PATTERN'
  | 'LIMITED_INFORMATION';

export interface BasisItem {
  answerRef: string;
  paraphrase: string;
  basisType: BasisType;
}

export type AssetReadiness = 'READY_TO_USE' | 'NEEDS_RECIPIENT_OR_DESTINATION_CHECK';

export interface AssetBlock {
  heading: string;
  body: string;
}

export interface PreparedAsset {
  title: string;
  copyButtonLabel: string;
  readiness: AssetReadiness;
  /** Stage 2 replaces these blocks with personalised drafting. Same shape. */
  blocks: AssetBlock[];
  prerequisiteChecks: string[];
}

export interface ResultContent {
  headline: string;
  startHereLabel: string;
  startHereBody: string;
  provisionalNote?: string;
  basis: BasisItem[];
  asset: PreparedAsset;
  firstAction: { instruction: string; approximateEffortMinutes: number };
  measurement: { mainObservation: string; window: string; limitation: string };
  decisionRule: { repeatWhen: string; adjustWhen: string; insufficientInformationWhen: string };
  whatCanWait: { activity: string; reason: string };
  missingInputs: string[];
}

export type OverviewState =
  | 'START_HERE'
  | 'NEXT_TO_CHECK'
  | 'NO_CLEAR_ISSUE_REPORTED'
  | 'NOT_ENOUGH_INFORMATION'
  | 'NOT_RELEVANT_YET';

export interface TraceEntry {
  playbookId: PlaybookId;
  status: 'SELECTED_PRIMARY' | 'SELECTED_SECONDARY' | 'ELIGIBLE_NOT_SELECTED' | 'BLOCKED';
  reasons: string[];
}

export interface SelectionTrace {
  rulesVersion: string;
  gateReached: string;
  gateNotes: string[];
  entries: TraceEntry[];
  tieRuleApplied: string | null;
}

export interface Selection {
  primary: PlaybookId;
  /** Everything else that cleared its bar, in priority order. */
  secondaries: PlaybookId[];
  overview: Record<Exclude<Category, 'BASELINE'>, { state: OverviewState; note: string }>;
  trace: SelectionTrace;
}

export interface PersonalisationField {
  id: string;
  label: string;
  helper?: string;
  kind: 'text' | 'textarea' | 'select';
  options?: { id: string; label: string }[];
  optional?: boolean;
}
